import { WebView } from '@zenbusiness/application-commons-webview';
import Button from '@zenbusiness/zui/Button';
import Heading from '@zenbusiness/zui/Heading';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import ConfirmModal from '#client/components/ConfirmModal';
import MoveMoneyChoiceViewModal from '#client/components/MoveMoney/MoveMoneyChoiceViewModal';
import { MODAL_NAMES } from '#client/constants/modals';
import { COUNTER_PARTY_STATUS_LABEL } from '#client/constants/payees';
import useBankingAccounts from '#client/hooks/useBankingAccounts';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useModalContext from '#client/hooks/useModalContext';
import { isActiveStatus, isNotRelinkableStatus } from '#client/utils/counterpartyStatus';
import { formatStringWithDot } from '#client/utils/text';

import {
  ButtonAreaStyled,
  ContentAreaStyled,
  DetailBoxLayoutStyled,
  TitleAreaStyled,
  UnLinkButtonStyled
} from './ConnectedAccountInfo.styles';
import PaymentStatusLabel from '../PaymentStatusLabel';

export default function ConnectedAccountInfo({
  id,
  bank,
  name,
  accountType,
  connectedType,
  accountNumber,
  fullAccountNumber,
  entryPageForSendMoney,
  wire,
  counterPartyStatus,
  onUnlinkConfirm,
  loading,
  isACHSupported,
  isWireSupported
}) {
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const { isWebView } = WebView;
  const { openModal } = useModalContext();
  // Treat any non-active status (archived, notRelinkable, etc.) the same way here,
  // consistent with LinkedBankAccount.js's handling of non-active counterparties.
  const isArchived = Boolean(counterPartyStatus) && !isActiveStatus(counterPartyStatus);
  const isNotRelinkable = isNotRelinkableStatus(counterPartyStatus);

  const { loading: isFetchingBankingAccounts } = useBankingAccounts();
  const handleUnlinkAccount = () => onUnlinkConfirm();

  const handleUnLinkAccount = () =>
    openModal({
      component: ConfirmModal,
      handleOK: handleUnlinkAccount,
      title: 'Are you sure you want to unlink this bank account from ZenBusiness Banking?',
      message: 'To add this bank account back to ZenBusiness Banking, you will need to relink.',
      okText: 'Unlink bank account',
      cancelText: 'Cancel'
    });

  const handleClickMoneyButton = () => {
    openModal({
      component: MoveMoneyChoiceViewModal,
      name: MODAL_NAMES.MOVE_MONEY_CHOICE_VIEW_MODAL,
      // when opening the modal from connected accounts page or
      // open from transaction history page
      // should hide check deposit option
      isHideCheckDepositOption: true,
      entryPageForSendMoney,
      counterParty: {
        id,
        bank,
        name,
        accountNumber,
        isArchived,
        accountType,
        connectedType,
        fullAccountNumber,
        wire,
        isACHSupported,
        isWireSupported
      }
    });
  };

  const disableMoveMoneyButton = isArchived || loading || !counterPartyStatus;

  return (
    <Stack>
      <DetailBoxLayoutStyled>
        <TitleAreaStyled spacing="16px">
          <Stack
            alignItems={isDesktop ? 'center' : 'flex-start'}
            direction={isDesktop ? 'row' : 'column'}
          >
            <Heading
              size="2xl"
              sx={{
                marginRight: '10px',
                fontWeight: 600
              }}
            >
              Bank Account Details
            </Heading>
            {isArchived && (
              <PaymentStatusLabel
                status={
                  isNotRelinkable
                    ? COUNTER_PARTY_STATUS_LABEL.CANNOT_BE_RELINKED
                    : COUNTER_PARTY_STATUS_LABEL.NO_LONGER_LINKED
                }
              />
            )}
          </Stack>
        </TitleAreaStyled>
        <ButtonAreaStyled
          alignItems="end"
          justifyContent="center"
        >
          <Button
            color="primary"
            disabled={disableMoveMoneyButton}
            disableRipple
            onClick={handleClickMoneyButton}
            variant="outlined"
          >
            Move Money
          </Button>
        </ButtonAreaStyled>
        <ContentAreaStyled>
          <Stack spacing="8px">
            <Text
              color="tertiary"
              size="xs"
            >
              Bank Name
            </Text>
            {!bank ? (
              <Skeleton />
            ) : (
              <Text
                color="primary"
                size="md"
              >
                {bank}
              </Text>
            )}
          </Stack>
          <Stack spacing="8px">
            <Text
              color="tertiary"
              size="xs"
            >
              Account number
            </Text>
            {!accountNumber ? (
              <Skeleton />
            ) : (
              <Text
                color="primary"
                size="md"
              >
                {formatStringWithDot(accountNumber ?? '')}
              </Text>
            )}
          </Stack>
          <Stack spacing="8px">
            <Text
              color="secondary"
              size="xs"
            >
              Transfer types
            </Text>
            <Text
              color="primary"
              size="md"
            >
              ACH
            </Text>
          </Stack>
          {loading || !counterPartyStatus ? (
            <Skeleton />
          ) : (
            !isArchived && (
              <Stack spacing="8px">
                <Text
                  color="tertiary"
                  size="xs"
                >
                  Used with
                </Text>
                <Stack>
                  <Stack
                    alignItems="center"
                    direction="row"
                    gap="24px"
                    justifyContent="space-between"
                  >
                    <Text
                      color="primary"
                      size="md"
                    >
                      ZenBusiness Banking
                    </Text>
                    {!isWebView && (
                      <UnLinkButtonStyled
                        color="info"
                        disabled={isFetchingBankingAccounts}
                        disableRipple
                        onClick={handleUnLinkAccount}
                        size="small"
                        variant="text"
                      >
                        Unlink
                      </UnLinkButtonStyled>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            )
          )}
        </ContentAreaStyled>
      </DetailBoxLayoutStyled>
    </Stack>
  );
}

ConnectedAccountInfo.propTypes = {
  id: PropTypes.string.isRequired,
  bank: PropTypes.string,
  name: PropTypes.string,
  accountType: PropTypes.string,
  connectedType: PropTypes.string,
  accountNumber: PropTypes.string,
  counterPartyStatus: PropTypes.string,
  loading: PropTypes.bool,
  onUnlinkConfirm: PropTypes.func.isRequired,
  fullAccountNumber: PropTypes.string,
  entryPageForSendMoney: PropTypes.string,
  isACHSupported: PropTypes.bool,
  isWireSupported: PropTypes.bool,
  wire: PropTypes.shape({
    address: PropTypes.shape({
      city: PropTypes.string,
      country: PropTypes.string,
      postalCode: PropTypes.string,
      state: PropTypes.string,
      street: PropTypes.string,
      street2: PropTypes.string
    }),
    routingNumber: PropTypes.string
  })
};
